import React from 'react';
import { styled } from '@linaria/react';

import { BREAKPOINTS } from '@/constants';
import { range } from '@/utils';

import Space from '@/components/Space';
import ClientOnlySnippet from '@/components/CodeSnippet/ClientOnlySnippet';

import BaseItem from './Item';
import ArrowDistance from './ArrowDistance';
import { BaseCodeWrapper } from './Grid.shared';

const NUM_OF_ROWS = 3;
const NUM_OF_COLS = 4;

function GridLinesGotcha() {
  const [gridRow] = React.useState('2 / 4');
  const [gridCol] = React.useState('1 / 4');

  const hasChild = gridRow !== null && gridCol !== null;

  const cssCode = `\
.child {
  grid-row: 2 / 4;
  grid-column: 1 / 4;
}`;

  return (
    <OuterWrapper as="section">
      <Row>
        <CodeWrapper>
          <ClientOnlySnippet
            omitCopyButton
            lockedToColorMode="dark"
            code={cssCode}
            lang="css"
          />
        </CodeWrapper>
        <DemoWrapper>
          <GridLayer>
            <GridRows>
              {range(1, NUM_OF_ROWS + 2).map((rowNum) => (
                <GridRow key={rowNum}>
                  <RowLabel>{rowNum}</RowLabel>
                </GridRow>
              ))}
            </GridRows>
            <GridCols>
              {range(1, NUM_OF_COLS + 2).map((rowNum) => (
                <GridCol key={rowNum}>
                  <ColLabel
                    style={{
                      left: rowNum === 1 ? 1.5 : '',
                    }}
                  >
                    {rowNum}
                  </ColLabel>
                </GridCol>
              ))}
            </GridCols>
          </GridLayer>
          <ChildLayer>
            <ChildWrapper>
              {hasChild && (
                <GridChild
                  layout={false}
                  style={{
                    gridRow,
                    gridColumn: gridCol,
                    opacity: 0.9,
                  }}
                >
                  <TopLabel>
                    <ArrowDistance
                      distance={226}
                      axis="horizontal"
                      animateTransitions={false}
                    />
                    <TopLabelText>Column 1 to 4</TopLabelText>
                  </TopLabel>
                  <SideLabel>
                    <ArrowDistance
                      distance={144}
                      axis="vertical"
                      animateTransitions={false}
                    />
                    <SideLabelText>Row 2 to 4</SideLabelText>
                  </SideLabel>
                </GridChild>
              )}
            </ChildWrapper>
          </ChildLayer>
        </DemoWrapper>
      </Row>
    </OuterWrapper>
  );
}

const OuterWrapper = styled(Space)`
  margin-inline: calc(var(--viewport-padding) * -1);
  margin-bottom: 64px;
  /* Extra space on top and on the right because the graph has axes that protrude beyond their containing block. */
  padding-top: 64px;
  padding-left: var(--viewport-padding);
  padding-right: calc(var(--viewport-padding) + 32px);
  padding-bottom: 48px;
  border-radius: 6px;
  background: var(--syntax-bg);
  outline: 1px solid var(--color-content-outline);
  outline-offset: -1px;

  html[data-color-mode='light'] & {
    @media ${BREAKPOINTS.smAndSmaller} {
      border-radius: 0px;
    }
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 64px;

  @media (max-width: 750px) {
    flex-direction: column-reverse;
    justify-content: unset;
    align-items: center;
    gap: 56px;
  }
`;

const DemoWrapper = styled.div`
  position: relative;
  width: 322px;
  max-width: 90%;
  aspect-ratio: ${NUM_OF_COLS} / ${NUM_OF_ROWS};
  /* There's a drag gesture here. Prevent standard behaviour */
  user-select: none;
`;

const Grid = styled.div`
  display: grid;
  grid-template-rows: repeat(${NUM_OF_ROWS}, 1fr);
  grid-template-columns: repeat(${NUM_OF_COLS}, 1fr);
  padding: 4px;
`;

const GridLayer = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 1px solid var(--color-gray-300);
  border-radius: 4px;
`;

const GridCols = styled.div`
  position: absolute;
  inset: 4px;
  display: flex;
  justify-content: space-between;
`;
const GridCol = styled.div`
  position: relative;
  margin-top: -16px;
  margin-bottom: -16px;
  border-left: 2px dashed var(--color-gray-500);
`;
const ColLabel = styled.div`
  position: absolute;
  top: -16px;
  left: 3px;
  transform: translate(-100%, -50%);
  font-weight: var(--font-weight-bold);
`;

const GridRows = styled(GridCols)`
  flex-direction: column;
`;
const GridRow = styled.div`
  position: relative;
  margin-left: -16px;
  margin-right: -16px;
  border-top: 2px dashed var(--color-gray-500);
`;
const RowLabel = styled.div`
  position: absolute;
  top: 0;
  right: -10px;
  transform: translate(100%, -55%);
  font-weight: var(--font-weight-bold);
`;

const ChildLayer = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  padding: 3px;
`;

const ChildWrapper = styled(Grid)`
  width: 100%;
  height: 100%;
`;

const GridChild = styled(BaseItem)``;

const TopLabel = styled.div`
  position: absolute;
  top: -8px;
  left: 0;
  right: 0;
  transform: translateY(-100%);

  @media (max-width: 27.6875rem) {
    display: none;
  }
`;
const TopLabelText = styled.div`
  position: absolute;
  inset: 0;
  margin: auto;
  width: max-content;
  padding: 4px 8px;
  background: var(--syntax-bg);
  font-size: 0.75rem;
  line-height: 20px;
  height: 28px;
  /* Optical alignment */
  transform: translateY(-1px);
`;

const SideLabel = styled.div`
  position: absolute;
  top: 2px;
  right: -8px;
  bottom: 0;
  transform: translateX(100%);

  @media (max-width: 27.6875rem) {
    display: none;
  }
`;
const SideLabelText = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  margin: auto;
  width: max-content;
  padding: 4px 8px;
  background: var(--syntax-bg);
  font-size: 0.75rem;
  line-height: 20px;
  height: 28px;
  transform: translateX(-3px) rotate(90deg) translateX(-6px);
  transform-origin: bottom left;
`;

const CodeWrapper = styled(BaseCodeWrapper)`
  min-width: 16rem;
`;

export default GridLinesGotcha;
